import * as React from "react"
import { useState } from "react"
import Checkbox, { Favorite, LockToggle } from "@imminently/decisively-ui/Checkbox"
import Switch from "@imminently/decisively-ui/Switch"
import { Label } from "@/ui/primitives/label"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage"

export function CheckboxPage() {
  const [checked, setChecked] = useState<boolean | "indeterminate">(false)
  const [disabled, setDisabled] = useState(false)

  const [favoriteChecked, setFavoriteChecked] = useState(false)
  const [lockChecked, setLockChecked] = useState(false)

  return (
    <ComponentPage
      title="Checkbox"
      description="A control that allows the user to toggle between checked and not checked."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with checkbox configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="playground-checkbox"
                checked={checked}
                onCheckedChange={setChecked}
                disabled={disabled}
              />
              <Label htmlFor="playground-checkbox">Accept terms and conditions</Label>
            </div>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Checked">
              <Switch
                checked={checked === true}
                onCheckedChange={(v) => setChecked(v)}
              />
            </ControlRow>
            <ControlRow label="Indeterminate">
              <Switch
                checked={checked === "indeterminate"}
                onCheckedChange={(v) => setChecked(v ? "indeterminate" : false)}
              />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch checked={disabled} onCheckedChange={setDisabled} />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* States */}
      <ComponentSection title="States" description="All checkbox states">
        <ComponentPreview className="gap-8">
          <VariantGrid columns={4}>
            <VariantItem label="Unchecked">
              <Checkbox />
            </VariantItem>
            <VariantItem label="Checked">
              <Checkbox checked />
            </VariantItem>
            <VariantItem label="Indeterminate">
              <Checkbox checked="indeterminate" />
            </VariantItem>
            <VariantItem label="Disabled">
              <Checkbox disabled />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* With Labels */}
      <ComponentSection title="With Labels" description="Checkbox with associated labels">
        <ComponentPreview className="flex-col gap-4 items-start">
          <div className="flex items-center space-x-2">
            <Checkbox id="terms" />
            <Label htmlFor="terms">Accept terms and conditions</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="marketing" defaultChecked />
            <Label htmlFor="marketing">Receive marketing emails</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="disabled-label" disabled />
            <Label htmlFor="disabled-label" className="text-muted-foreground">
              Disabled option
            </Label>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Favorite Component */}
      <ComponentSection
        title="Favorite"
        description="A star-based toggle for favoriting items"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="flex items-center gap-4">
              <Favorite
                checked={favoriteChecked}
                onCheckedChange={setFavoriteChecked}
              />
              <span className="text-sm">
                {favoriteChecked ? "Favorited" : "Not favorited"}
              </span>
            </div>
          </ComponentPreview>

          <ComponentPreview className="gap-8">
            <VariantGrid columns={2}>
              <VariantItem label="Unchecked">
                <Favorite />
              </VariantItem>
              <VariantItem label="Checked">
                <Favorite checked />
              </VariantItem>
            </VariantGrid>
          </ComponentPreview>
        </div>
      </ComponentSection>

      {/* Lock Component */}
      <ComponentSection
        title="Lock Toggle"
        description="A lock/unlock toggle for locking items"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="flex items-center gap-4">
              <LockToggle
                checked={lockChecked}
                onCheckedChange={setLockChecked}
              />
              <span className="text-sm">
                {lockChecked ? "Locked" : "Unlocked"}
              </span>
            </div>
          </ComponentPreview>

          <ComponentPreview className="gap-8">
            <VariantGrid columns={2}>
              <VariantItem label="Unlocked">
                <LockToggle checked={false} />
              </VariantItem>
              <VariantItem label="Locked">
                <LockToggle checked />
              </VariantItem>
            </VariantGrid>
          </ComponentPreview>
        </div>
      </ComponentSection>

      {/* Form Example */}
      <ComponentSection title="Form Example" description="Checkboxes in a form context">
        <ComponentPreview className="flex-col gap-4 items-start">
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Notification Preferences</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox id="email-notif" defaultChecked />
                <Label htmlFor="email-notif">Email notifications</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="sms-notif" />
                <Label htmlFor="sms-notif">SMS notifications</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="push-notif" defaultChecked />
                <Label htmlFor="push-notif">Push notifications</Label>
              </div>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
